import type {
  AdditionalSpaceObservingSession,
  RoomObservingSession,
  SketchingSessionRoom,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import type { ItemPickerInstance } from '@miso/design-system';
import { resolveRoomObservingSessionForSketchRoom } from '@/entities/visit-counsel/model/resolveRoomObservingSessionForSketchRoom';
import { hasObservingSessionMovingItemsConfirmed } from '@/entities/visit-counsel/model/observing-session-moving-items';
import {
  formatSketchingRoomLabelCompact,
  formatSketchingRoomLabelSpaced,
} from '@/entities/visit-counsel/model/sketchRoomDisplayLabel';
import {
  isSingletonRoomType,
  recoverRoomType,
} from '@/entities/visit-counsel/model/recoverRoomType';
import { isFormalAdditionalSpaceType } from '@/entities/visit-counsel/model/formalAdditionalSpace';
import { SPACE_TYPE_LABELS } from '@/features/visit-counsel/counsel-progress/model/counsel-progress.constants';

/**
 * 방 옵저빙 순서 선택 화면용 picker 항목.
 *
 * `kind === 'sketch'` 는 sketching 단계에서 만들어진 일반 방(거실/주방/방 등)으로
 * 짐 기록·영상은 `RoomObservingSession` 으로 진행한다. `kind === 'additional'` 은
 * `AdditionalSpaceObservingSession` 기반 항목으로, 두 종류로 나뉜다.
 *
 *   1. linked: sketching 단계에서 "+ 공간 추가" 로 등록된 룸(BATH_ROOM /
 *      BALCONY / RESIDENCE_STORAGE / OTHER_SPACE)의 옵저빙 세션. backend 는
 *      이런 룸에 대해 sketching 룸 entry + 같은 roomId 의 additionalSpaceObserving
 *      세션을 동시에 만든다 — picker 는 둘을 하나로 합쳐 sketching 라벨/이모지를
 *      보이고 navigation 은 additional space API 로 진행한다 (`linkedSketchRoom`
 *      이 채워진 entry).
 *   2. unlinked (운반 경로 등): 사다리차 진입로/계단/엘리베이터 등 sketching 룸과
 *      매칭되지 않는 추가공간 세션 — `linkedSketchRoom` 없이 spaceType 기반 라벨/이모지.
 */
export type ObservingPickerEntry =
  | {
      kind: 'sketch';
      itemId: string;
      title: string;
      /**
       * 카드 title 아래에 작게 표시할 보조 텍스트.
       *
       * 주: 사용자가 sketching 단계에서 ROOM 에 "화장실 있음"·"발코니 있음" 같은
       * 부속 시설을 선택하면 여기에 콤마로 join 해서 노출할 예정. 다만 현재는
       * `RoomSketchingRequest` API 가 attributes 를 받지 않고
       * `SketchingSessionRoom` 에도 tags/attributes 필드가 없어 데이터가 전달되지
       * 않으므로 항상 undefined. backend 와 SketchingSessionRoom 에 attributes 가
       * 추가되면 `makeSketchEntry` 에서 한 줄 매핑하면 된다.
       */
      description?: string;
      iconEmoji: string;
      done: boolean;
      hasItems: boolean;
      hasVideo: boolean;
      sessionId: string | null;
      room: SketchingSessionRoom;
      session: RoomObservingSession | null;
    }
  | {
      kind: 'additional';
      itemId: string;
      title: string;
      description?: string;
      iconEmoji: string;
      done: boolean;
      hasItems: boolean;
      hasVideo: boolean;
      sessionId: string;
      session: AdditionalSpaceObservingSession;
      orderIndex: number;
      /**
       * sketching 단계의 룸과 같은 물리적 공간이라면 그 sketching 룸을 함께 담는다.
       *
       * - 라벨: sketching 의 `formatSketchingRoomLabelSpaced` 로 노출 ("1번 세대창고")
       * - 이모지: sketching 룸 type 기준 ROOM_ICON_BY_TYPE
       * - 섹션 분류: sketching 룸 type 기준 (BATH_ROOM → 주요공간 등)
       *
       * `linkedSketchRoom` 이 없는 entry 는 운반 경로(사다리차 등) — 항상 추가공간 섹션.
       */
      linkedSketchRoom?: SketchingSessionRoom;
    };

export interface ObservingPickerSection {
  id: 'primary' | 'rooms' | 'additional';
  title: string;
  entries: ObservingPickerEntry[];
}

/**
 * 사용자 요청 분류 — "주요공간 / 방 / 추가공간".
 *
 * - 주요공간: 거실/주방/화장실 (현관 ENTRANCE는 사전 자동 진행 대상이라 모든 섹션에서 제외)
 * - 방: ROOM
 * - 추가공간: sketching 의 그 외 룸타입(BALCONY/RESIDENCE_STORAGE/OTHER_SPACE) + 인테이크 후 추가된 옵저빙 세션
 */
const PRIMARY_TYPES = new Set<string>(['LIVING_ROOM', 'KITCHEN', 'BATH_ROOM']);
const ROOM_TYPES_FOR_SECTION = new Set<string>(['ROOM']);
/** 모든 섹션에서 제외 — 현관은 자동 우선 진행 대상 */
const EXCLUDED_TYPES = new Set<string>(['ENTRANCE']);

const ROOM_ICON_BY_TYPE: Record<string, string> = {
  LIVING_ROOM: '🛋️',
  KITCHEN: '🍳',
  // 🛌(자는 사람 + 침대) — 단순 침대 🛏️ 보다 다수 환경에서 컬러 글리프로 안정적으로 노출.
  ROOM: '🛌',
  BATH_ROOM: '🚿',
  BALCONY: '🪟',
  RESIDENCE_STORAGE: '📦',
  OTHER_SPACE: '📦',
};

const ADDITIONAL_SPACE_ICON_BY_TYPE: Record<string, string> = {
  LADDER_TRUCK_ENTRANCE: '🚚',
  FIRST_FLOOR_TRUCK_ENTRANCE: '🛻',
  STAIRS: '🪜',
  // sketching 룸 타입과 동일한 spaceType 으로 들어오는 추가공간(예: 세대창고 단독 옵저빙)도
  // sketching 측 이모지와 일치시키기 위해 함께 등록.
  RESIDENCE_STORAGE: '📦',
  BATH_ROOM: '🚿',
  BALCONY: '🪟',
  OTHER_SPACE: '📦',
};

/**
 * picker entry 배열을 만든다. sketching 룸은 항상 `roomId` 가 안정적인 itemId 로 사용되며,
 * additional 세션은 session.id 로 식별된다.
 */
function makeSketchEntry(
  room: SketchingSessionRoom,
  rooms: SketchingSessionRoom[],
  roomObserving: RoomObservingSession[]
): ObservingPickerEntry {
  const session = resolveRoomObservingSessionForSketchRoom(room, rooms, roomObserving);
  const hasVideo = !!session?.video;
  const hasItems = hasObservingSessionMovingItemsConfirmed(session);
  // 분류와 이모지 모두 회복된 type 기준 — 과거 매퍼 버그로 OTHER_SPACE 로 저장된 ROOM 등을 제자리로 보낸다.
  const effectiveType = recoverRoomType(room);
  // 같은 type 의 룸이 1개뿐이면 ordinal prefix 를 떼고 베이스 한국어만 표시한다 (사용자 정책).
  const singleton = isSingletonRoomType(room, rooms);
  // status === 'COMPLETED' 는 짐 기록 + 영상 촬영이 서버 측에서 모두 확정된 상태.
  // hasVideo 는 refetch 타이밍에 따라 영상 토큰이 아직 응답에 없을 수 있어 단독으로는
  // 신뢰하기 어렵다. COMPLETED 상태를 우선 확인해 `done` 을 결정한다.
  const done = session != null && (session.status === 'COMPLETED' || (hasVideo && hasItems));
  return {
    kind: 'sketch',
    itemId: room.roomId ?? `${room.type}-${room.namePrefix}-${room.index}`,
    title: formatSketchingRoomLabelSpaced(room, { singleton }),
    description: resolveSketchRoomDescription(room),
    iconEmoji: ROOM_ICON_BY_TYPE[effectiveType] ?? '📦',
    done,
    hasItems,
    hasVideo,
    sessionId: session?.id ?? null,
    room,
    session: session ?? null,
  };
}

/**
 * SketchingSessionRoom → 카드 보조 텍스트.
 *
 * 현재 데이터 모델에는 ROOM 별 부속 시설(화장실/발코니) attribute 가 저장되지
 * 않아 항상 `undefined` 를 반환한다. SketchingSessionRoom 에 `attributes` 같은
 * 필드가 추가되는 시점에 여기서 한 줄로 매핑하면 design-system 의 ItemPickerItem
 * description 슬롯이 카드 아래 회색 보조 텍스트로 그대로 렌더한다.
 */
function resolveSketchRoomDescription(_room: SketchingSessionRoom): string | undefined {
  // TODO(부속 시설 description): RoomSketchingRequest 와 SketchingSessionRoom 에
  // attributes (TOILET / BALCONY 등) 가 추가되면 다음과 같이 매핑한다.
  //   const tags: string[] = [];
  //   if (_room.attributes?.TOILET) tags.push('화장실');
  //   if (_room.attributes?.BALCONY) tags.push('발코니');
  //   return tags.length > 0 ? tags.join(', ') : undefined;
  return undefined;
}

function additionalSessionDoneness(session: AdditionalSpaceObservingSession): {
  hasVideo: boolean;
  hasItems: boolean;
  done: boolean;
} {
  const hasVideo = !!session?.video;
  const hasItems =
    session.canRecordMovingItems !== true
      ? false
      : (session.movingItems?.length ?? 0) > 0 || (session.movingItemSpecIds?.length ?? 0) > 0;
  // status === 'COMPLETED' 를 우선 확인 (refetch 타이밍 이슈로 video 필드가 없을 수 있음).
  const done =
    session.status === 'COMPLETED' ||
    (session.canRecordMovingItems !== true ? hasVideo : hasVideo && hasItems);
  return { hasVideo, hasItems, done };
}

function makeAdditionalEntry(
  session: AdditionalSpaceObservingSession,
  orderIndex: number
): ObservingPickerEntry {
  const { hasVideo, hasItems, done } = additionalSessionDoneness(session);
  return {
    kind: 'additional',
    itemId: `additional:${session.id}`,
    title: SPACE_TYPE_LABELS[session.spaceType] ?? session.spaceType,
    iconEmoji: ADDITIONAL_SPACE_ICON_BY_TYPE[session.spaceType] ?? '📦',
    done,
    hasItems,
    hasVideo,
    sessionId: session.id,
    session,
    orderIndex,
  };
}

/**
 * sketching 룸 + 그 룸과 같은 roomId 의 additionalSpaceObservingSession 을 합쳐
 * 하나의 picker entry 를 만든다.
 *
 * - 라벨/이모지/description: sketching 룸 메타데이터 기준 (보다 풍부한 표시).
 * - 진행 상태/sessionId: additionalSpaceObservingSession 기준 (실제 작업 단위).
 * - itemId: sketching 룸의 roomId 가 안정적이라 그것을 우선 사용.
 */
function makeLinkedAdditionalEntry(
  room: SketchingSessionRoom,
  session: AdditionalSpaceObservingSession,
  rooms: SketchingSessionRoom[],
  orderIndex: number
): ObservingPickerEntry {
  const { hasVideo, hasItems, done } = additionalSessionDoneness(session);
  const effectiveType = recoverRoomType(room);
  // linked-additional 도 sketching 룸 기준으로 같은 type 인스턴스 수를 카운트한다.
  // 단독이면 "세대창고"·"다락방" 처럼 ordinal 없이 노출.
  const singleton = isSingletonRoomType(room, rooms);
  return {
    kind: 'additional',
    itemId: room.roomId ?? `additional:${session.id}`,
    title: formatSketchingRoomLabelSpaced(room, { singleton }),
    description: resolveSketchRoomDescription(room),
    iconEmoji:
      ROOM_ICON_BY_TYPE[effectiveType] ?? ADDITIONAL_SPACE_ICON_BY_TYPE[session.spaceType] ?? '📦',
    done,
    hasItems,
    hasVideo,
    sessionId: session.id,
    session,
    orderIndex,
    linkedSketchRoom: room,
  };
}

export interface BuildObservingPickerSectionsParams {
  /** sketching 의 룸 (정렬은 호출부 입력 그대로 사용 — 보통 sketching 입력 순서) */
  rooms: SketchingSessionRoom[];
  /** 방 옵저빙 세션 — sketching 룸과 roomId/타입+ordinal 로 매핑 */
  roomObserving: RoomObservingSession[];
  /** 인테이크 후 생성된 추가공간 옵저빙 세션 — 호출부에서 정렬해 넘김 */
  additionalSpaceObserving: AdditionalSpaceObservingSession[];
}

/**
 * entry 의 섹션 분류용 effective type.
 *
 * - sketch entry: sketching 룸 type (회복 적용).
 * - linked-additional entry: 연결된 sketching 룸 type (회복 적용).
 * - unlinked-additional entry: null — PRIMARY/ROOM 모두 false → 추가공간 섹션으로 fall-through.
 */
function effectiveTypeForEntry(entry: ObservingPickerEntry): string | null {
  if (entry.kind === 'sketch') return recoverRoomType(entry.room);
  if (entry.linkedSketchRoom) return recoverRoomType(entry.linkedSketchRoom);
  return null;
}

/**
 * 사용자 요청에 맞춰 "주요공간 / 방 / 추가공간" 3 섹션으로 정리된 entry 배열을 반환한다.
 *
 * - 현관(ENTRANCE)은 모든 섹션에서 제외 (사전 자동 진행 대상).
 * - 같은 물리적 공간이 sketching 룸 + additionalSpaceObserving 양쪽에 존재하는
 *   ADDITIONAL_ROOM_TYPES (BATH_ROOM/BALCONY/RESIDENCE_STORAGE/OTHER_SPACE) 는
 *   `roomId` 매칭으로 dedupe 되어 한 번만 노출된다 — entry kind 는 'additional'
 *   (실제 navigation 이 additional space API 로 이루어지기 때문) 이고, 라벨/이모지/
 *   섹션 분류는 sketching 룸 메타를 따른다.
 * - 빈 섹션은 결과에서 제외한다 — 시각적 노이즈를 줄이기 위함.
 */
export function buildObservingPickerSections({
  rooms,
  roomObserving,
  additionalSpaceObserving,
}: BuildObservingPickerSectionsParams): ObservingPickerSection[] {
  // 같은 물리적 공간을 두 entry 로 만들지 않도록 roomId 매핑을 미리 구축한다.
  // additional 세션의 roomId 는 sketching 룸의 roomId 와 동일한 식별자이며,
  // 운반 경로(사다리차 진입로 등) 처럼 룸과 무관한 세션은 roomId 가 null/undefined.
  const additionalByRoomId = new Map<string, AdditionalSpaceObservingSession>();
  for (const s of additionalSpaceObserving) {
    if (s.roomId) additionalByRoomId.set(s.roomId, s);
  }

  const consumedAdditionalIds = new Set<string>();

  // sketching 룸 순서대로 entry 를 만든다 — UI 순서가 sketching 입력 순서를 따른다.
  // ENTRANCE 는 모든 섹션에서 제외 (사전 자동 진행 대상).
  // 공식 추가공간 (BALCONY/RESIDENCE_STORAGE) 은 pre-progress picker 에서 제외하고
  // `progress` 단계 이후 `additional-spaces` stage 의 picker 가 별도로 노출한다
  // (`formalAdditionalSpace.ts` 의 정책 — 사용자가 progress 7-stage 를 먼저 마친 뒤
  //  보조 공간을 묶어서 촬영).
  let nextOrderIndex = 0;
  const orderedEntries: ObservingPickerEntry[] = [];
  for (const room of rooms) {
    if (EXCLUDED_TYPES.has(recoverRoomType(room))) continue;
    const additional = room.roomId ? additionalByRoomId.get(room.roomId) : undefined;
    if (additional) {
      // linked-additional: sketching 룸 type 과 additional 세션 spaceType 이
      // 사실상 동일하지만 (둘 다 `RESIDENCE_STORAGE` 등), 안전하게 둘 다 검사한다.
      // OTHER_SPACE 는 사용자 자유 입력 방이므로 이름("발코니" 등)에 관계없이
      // 공식 추가공간으로 취급하지 않는다 — additional.spaceType·recovered type
      // 어느 쪽으로 BALCONY 등이 나오더라도 pre-progress 에 포함한다.
      // isAdditionalSpace === false 이면 백엔드가 ROOM_OBSERVING 세션을 붙인 것이므로
      // type 이 BALCONY 등이어도 pre-progress 에 포함한다.
      if (
        room.isAdditionalSpace !== false &&
        room.type !== 'OTHER_SPACE' &&
        (isFormalAdditionalSpaceType(additional.spaceType) ||
          isFormalAdditionalSpaceType(recoverRoomType(room)))
      ) {
        // 공식 추가공간 — pre-progress picker 노출에서 제외 (post-progress 에서 노출).
        consumedAdditionalIds.add(additional.id);
        continue;
      }
      orderedEntries.push(makeLinkedAdditionalEntry(room, additional, rooms, nextOrderIndex++));
      consumedAdditionalIds.add(additional.id);
    } else {
      // sketching 룸이지만 같은 roomId 의 additional 이 없는 경우 — 회복된 type 이
      // 공식 추가공간이면 pre-progress 에서 숨긴다.
      // 단, isAdditionalSpace === false 이면 백엔드가 ROOM_OBSERVING 세션을 붙인 것이므로
      // type 이 BALCONY 등이어도 pre-progress 에 포함한다.
      // OTHER_SPACE 는 사용자 자유 입력 방이므로 이름("발코니" 등)에 관계없이 항상 포함.
      if (
        room.isAdditionalSpace !== false &&
        room.type !== 'OTHER_SPACE' &&
        isFormalAdditionalSpaceType(recoverRoomType(room))
      )
        continue;
      orderedEntries.push(makeSketchEntry(room, rooms, roomObserving));
    }
  }
  // 남은 추가공간 세션(roomId 가 없거나 sketching 룸과 매칭되지 않는 케이스 — 운반 경로 등)
  // 공식 추가공간이 unlinked 로 들어오는 경우(이론상 드묾)에도 동일하게 제외한다.
  for (const s of additionalSpaceObserving) {
    if (consumedAdditionalIds.has(s.id)) continue;
    if (isFormalAdditionalSpaceType(s.spaceType)) continue;
    orderedEntries.push(makeAdditionalEntry(s, nextOrderIndex++));
  }

  const primaryEntries = orderedEntries.filter((e) => {
    const t = effectiveTypeForEntry(e);
    return t != null && PRIMARY_TYPES.has(t);
  });
  const roomEntries = orderedEntries.filter((e) => {
    const t = effectiveTypeForEntry(e);
    return t != null && ROOM_TYPES_FOR_SECTION.has(t);
  });
  const additionalEntries = orderedEntries.filter((e) => {
    const t = effectiveTypeForEntry(e);
    if (t == null) return true; // unlinked additional → 항상 추가공간
    return !PRIMARY_TYPES.has(t) && !ROOM_TYPES_FOR_SECTION.has(t);
  });

  const sections: ObservingPickerSection[] = [];
  if (primaryEntries.length > 0) {
    sections.push({ id: 'primary', title: '주요공간', entries: primaryEntries });
  }
  if (roomEntries.length > 0) {
    sections.push({ id: 'rooms', title: '방', entries: roomEntries });
  }
  if (additionalEntries.length > 0) {
    sections.push({
      id: 'additional',
      title: '추가공간',
      entries: additionalEntries,
    });
  }
  return sections;
}

/**
 * 완료된 entry → ItemPickerInstance prefill.
 *
 * navigation 모드의 ItemPicker 는 `instances.length > 0` 인 카드를 자동으로 "완료" badge로
 * 렌더하므로, 완료된 항목마다 dummy instance 한 개씩 채워주면 시각적 done 상태를
 * 별도 props 없이 그대로 표현할 수 있다.
 */
export function entriesToDoneInstances(sections: ObservingPickerSection[]): ItemPickerInstance[] {
  const out: ItemPickerInstance[] = [];
  for (const sec of sections) {
    for (const e of sec.entries) {
      if (!e.done) continue;
      out.push({
        instanceId: `done_${e.itemId}`,
        itemId: e.itemId,
        answers: {},
      });
    }
  }
  return out;
}

/**
 * sketch entry 의 leaf 라벨(체크리스트 등에서 사용하는 "1번방" 형태)
 */
export function compactLabelForEntry(entry: ObservingPickerEntry): string {
  if (entry.kind === 'sketch') return formatSketchingRoomLabelCompact(entry.room);
  return entry.title;
}

/**
 * 모든 entry 가 완료되었는지 — 어댑터가 "다음 단계로 진행" 시점을 결정할 때 사용.
 */
export function allEntriesDone(sections: ObservingPickerSection[]): boolean {
  return sections.every((s) => s.entries.every((e) => e.done));
}

/**
 * 전체 entry 수 / 완료 수 카운트.
 */
export function countEntries(sections: ObservingPickerSection[]): {
  total: number;
  done: number;
} {
  let total = 0;
  let done = 0;
  for (const s of sections) {
    for (const e of s.entries) {
      total += 1;
      if (e.done) done += 1;
    }
  }
  return { total, done };
}
